import { useState } from "react";
import type { ListingTemplateProduct } from "@/lib/listing-templates";
import { Stars, TemplatePoweredFooter } from "./shared";
import { imageAt, brandOf, trustSections } from "./template-helpers";

/**
 * Template 6 — Soft Pastel.
 * Airy, light-coloured layout with a soft pastel palette, rounded cards and
 * generous whitespace. Friendly and approachable, suited to home, gift,
 * lifestyle and handmade products.
 */
export default function SoftPastelTemplate({ product }: { product: ListingTemplateProduct }) {
  const [activeImage, setActiveImage] = useState(0);
  const hero = imageAt(product, activeImage) ?? imageAt(product, 0);
  const brand = brandOf(product);
  const sections = trustSections(product);

  return (
    <div className="bg-rose-50/40 text-rose-950 font-sans">
      {/* Header */}
      <header className="border-b border-rose-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-rose-200 flex items-center justify-center">
              <i className="ri-heart-3-line text-rose-600" />
            </span>
            <span className="font-semibold tracking-tight">{brand ?? "Store"}</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-rose-400">
            <span className="cursor-pointer hover:text-rose-700">Shop</span>
            <span className="cursor-pointer hover:text-rose-700">About</span>
            <span className="cursor-pointer hover:text-rose-700">Support</span>
          </nav>
        </div>
      </header>

      {/* Product hero */}
      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            {hero && (
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-rose-100 border border-rose-100">
                <img src={hero.src} alt={hero.alt} data-testid="template-hero-image" className="w-full h-full object-contain object-center" />
              </div>
            )}
            {product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {product.images.slice(0, 5).map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`aspect-square rounded-xl overflow-hidden border-2 ${
                      activeImage === i ? "border-rose-400" : "border-transparent hover:border-rose-200"
                    }`}
                  >
                    <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-5">
            <div>
              {brand && (
                <p className="text-xs font-semibold uppercase tracking-widest text-rose-500">{brand}</p>
              )}
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight mt-1 break-words">
                {product.title}
              </h1>
              <div className="mt-3">
                <Stars score={product.rating.score} count={product.rating.count} />
              </div>
            </div>

            <div className="flex items-end gap-3">
              <span className="text-3xl font-bold text-rose-600">{product.price}</span>
              {product.wasPrice && <span className="text-lg text-rose-300 line-through">{product.wasPrice}</span>}
            </div>

            {product.shortDescription && (
              <p className="text-rose-800/70 leading-relaxed">{product.shortDescription}</p>
            )}

            {product.keyFeatures.length > 0 && (
              <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4 space-y-2">
                {product.keyFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-2.5 text-sm text-rose-900">
                    <i className="ri-check-line text-rose-500 mt-0.5" />
                    <span className="break-words">{feature}</span>
                  </div>
                ))}
              </div>
            )}

            <button className="w-full py-3.5 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-full flex items-center justify-center gap-2">
              <i className="ri-shopping-cart-line" /> Add to cart
            </button>

            <div className="grid grid-cols-3 gap-2 text-center text-xs text-rose-500">
              <div className="rounded-2xl bg-white border border-rose-100 py-2.5">
                <i className="ri-truck-line block text-lg text-rose-400 mb-1" />
                Fast dispatch
              </div>
              <div className="rounded-2xl bg-white border border-rose-100 py-2.5">
                <i className="ri-refresh-line block text-lg text-rose-400 mb-1" />
                Easy returns
              </div>
              {product.warranty && (
                <div className="rounded-2xl bg-white border border-rose-100 py-2.5">
                  <i className="ri-shield-check-line block text-lg text-rose-400 mb-1" />
                  {product.warranty}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {product.fullDescription.length > 0 && (
          <section className="mt-16 max-w-3xl">
            <h2 className="text-xl font-semibold tracking-tight mb-4">About this product</h2>
            <div className="space-y-4 text-rose-900/70 leading-relaxed">
              {product.fullDescription.map((para) => (
                <p key={para.slice(0, 24)}>{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* Specifications */}
        {product.specifications.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl font-semibold tracking-tight mb-5">Specifications</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {product.specifications.map((spec) => (
                <div key={spec.label} className="rounded-2xl bg-white border border-rose-100 p-4">
                  <p className="text-xs text-rose-400">{spec.label}</p>
                  <p className="text-sm font-medium text-rose-900 mt-1 break-words">{spec.value}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Shipping / returns / support */}
        {sections.length > 0 && (
          <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {sections.map((section) => (
              <div key={section.title} className="rounded-2xl bg-white border border-rose-100 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center">
                    <i className="ri-heart-3-line text-rose-500" />
                  </span>
                  <h3 className="font-semibold text-rose-900">{section.title}</h3>
                </div>
                <ul className="space-y-2 text-sm text-rose-800/70">
                  {section.lines.map((line) => (
                    <li key={line} className="leading-relaxed break-words">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="border-t border-rose-100">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <TemplatePoweredFooter
            testId="soft-pastel-footer"
            left={brand ?? "Store"}
            sku={product.sku}
            className="text-rose-400"
            centerClassName="text-center text-rose-300"
          />
        </div>
      </footer>
    </div>
  );
}